import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, RefreshCw, Plus, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_PACKAGES = [
  { id: "tokens_50", tokens: 50, price: 5, popular: false },
  { id: "tokens_100", tokens: 100, price: 9, popular: true },
  { id: "tokens_250", tokens: 250, price: 19, popular: false },
  { id: "tokens_500", tokens: 500, price: 35, popular: false },
];

export function TokenCounter({ className, isDemo = false }) {
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const fetchTokens = useCallback(async () => {
    try {
      const response = await fetch(`${API}/ai/tokens`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens);
      }
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await fetch(`${API}/ai/tokens/reset`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens);
        toast.success("Tokens reset to 100!");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to reset tokens");
      }
    } catch (error) {
      console.error("Failed to reset tokens:", error);
      toast.error("Failed to reset tokens");
    } finally {
      setResetting(false);
    }
  };

  const handlePurchase = async (pkg) => {
    setSelectedPackage(pkg.id);
    setPurchasing(true);
    try {
      const response = await fetch(`${API}/ai/tokens/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          package_id: pkg.id,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          // Tokens added directly (demo mode)
          setTokens(data.tokens);
          setShowBuyDialog(false);
          toast.success(`Added ${pkg.tokens} tokens!`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to purchase tokens");
      }
    } catch (error) {
      toast.error("Failed to purchase tokens");
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  const getTokenColor = () => {
    if (tokens === null) return "text-zinc-400";
    if (tokens >= 50) return "text-emerald-400";
    if (tokens >= 20) return "text-amber-400";
    return "text-red-400";
  };

  const getProgressWidth = () => {
    if (tokens === null) return "0%";
    return `${Math.min((tokens / 100) * 100, 100)}%`;
  };

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5", className)}>
        <div className="w-4 h-4 animate-pulse bg-zinc-700 rounded" />
        <div className="w-12 h-4 animate-pulse bg-zinc-700 rounded" />
      </div>
    );
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors",
                className
              )}
              onClick={() => setShowBuyDialog(true)}
            >
              <Sparkles className={cn("w-4 h-4", getTokenColor())} />
              <div className="flex flex-col">
                <span className={cn("text-sm font-semibold tabular-nums", getTokenColor())}>
                  {tokens !== null ? tokens : "—"}
                </span>
              </div>
              <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className={cn("h-full rounded-full transition-all duration-300", 
                    tokens >= 50 ? "bg-emerald-500" : tokens >= 20 ? "bg-amber-500" : "bg-red-500"
                  )}
                  style={{ width: getProgressWidth() }}
                />
              </div>
              <Plus className="w-3.5 h-3.5 text-zinc-400" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-[#18181B] border-white/10 p-3">
            <div className="space-y-2">
              <p className="text-white font-medium">AI Tokens: {tokens}</p>
              <p className="text-zinc-400 text-xs">
                {tokens >= 50 
                  ? "Plenty of tokens remaining" 
                  : tokens >= 20 
                    ? "Running low on tokens" 
                    : "Almost out of tokens!"}
              </p>
              <p className="text-zinc-400 text-xs">Each AI suggestion uses 5 tokens</p>
              <p className="text-violet-400 text-xs font-medium">Click to buy more tokens</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Buy Tokens Dialog */}
      <Dialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              AI Tokens
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Purchase tokens to unlock AI-powered campaign suggestions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Balance */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <p className="text-zinc-400 text-sm">Current Balance</p>
              <div className="flex items-center gap-2 mt-1">
                <Sparkles className={cn("w-5 h-5", getTokenColor())} />
                <span className={cn("text-2xl font-bold tabular-nums", getTokenColor())}>
                  {tokens}
                </span>
                <span className="text-zinc-400">tokens</span>
              </div>
            </div>

            {/* Token Packages */}
            <div className="space-y-2">
              <p className="text-zinc-300 text-sm font-medium">Buy Token Packages</p>
              <div className="grid grid-cols-2 gap-2">
                {TOKEN_PACKAGES.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing}
                    className={cn(
                      "relative p-3 rounded-lg border-2 text-left transition-all hover:border-violet-500/50",
                      pkg.popular 
                        ? "border-violet-500/50 bg-violet-500/10" 
                        : "border-white/10 bg-black/30 hover:bg-white/5"
                    )}
                  >
                    {pkg.popular && (
                      <span className="absolute -top-2 right-2 px-2 py-0.5 bg-violet-500 text-white text-[10px] font-bold rounded">
                        POPULAR
                      </span>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-violet-400" />
                      <span className="text-white font-bold">{pkg.tokens}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-emerald-400 font-semibold">${pkg.price}</span>
                      <span className="text-zinc-400 text-xs">
                        ${(pkg.price / pkg.tokens * 100).toFixed(0)}¢/token
                      </span>
                    </div>
                    {purchasing && selectedPackage === pkg.id && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Demo-only token reset */}
            {isDemo && (
              <>
                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-[#0A0A0A] px-3 text-zinc-400">or for demo</span>
                  </div>
                </div>

                {/* Reset Button */}
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={resetting}
                  data-testid="token-reset-btn"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  {resetting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reset to 100 (Demo)
                </Button>
              </>
            )}

            <p className="text-zinc-500 text-xs text-center">
              Tokens never expire. Each AI suggestion costs 5 tokens.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Export a hook for other components to use
export function useTokens() {
  const [tokens, setTokens] = useState(null);

  const fetchTokens = useCallback(async () => {
    try {
      const response = await fetch(`${API}/ai/tokens`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens);
        return data.tokens;
      }
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
    }
    return null;
  }, []);

  const deductTokens = async (amount = 5) => {
    try {
      const response = await fetch(`${API}/ai/tokens/deduct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount })
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens);
        return data;
      }
    } catch (error) {
      console.error("Failed to deduct tokens:", error);
    }
    return null;
  };

  // Fetch tokens on mount using a ref to track initialization
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchTokens();
    }
  }, [fetchTokens]);

  return { tokens, fetchTokens, deductTokens, setTokens };
}
